/**
 * Status, filter, and display config for field event summaries.
 */
import { FileEdit, CheckCircle2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { EventSummaryStatus, EventSummaryFilters } from '@/types/event-summary.types';
import type { BadgeVariant } from '@/components/common/LabelBadge';

/** Badge + icon config for an event-summary status. */
export interface EventSummaryStatusConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor?: string;
}

/** Badge config for each event-summary status. */
export const EVENT_SUMMARY_STATUS_CONFIG: Record<EventSummaryStatus, EventSummaryStatusConfig> = {
  not_filled: {
    label: 'Non saisi',
    icon: FileEdit,
    badgeVariant: 'on-hold',
    badgeColor: 'rgb(var(--status-pending))',
  },
  filled: {
    label: 'Saisi',
    icon: CheckCircle2,
    badgeVariant: 'active',
    badgeColor: 'rgb(var(--status-success))',
  },
};

/** Default event-summary list filters. */
export const DEFAULT_EVENT_SUMMARY_FILTERS: EventSummaryFilters = {
  statuses: [],
  dateRange: 'month',
  customDateFrom: undefined,
  customDateTo: undefined,
  establishmentIds: [],
};

/** Filter group IDs for the event-summary {@link DataTable} configure panel. */
export type FilterGroupId =
  | 'filter-period'
  | 'filter-date-range'
  | 'filter-status'
  | 'filter-establishment';

/** Configure-panel order for event-summary filter groups. */
export const ALL_FILTER_GROUP_IDS: FilterGroupId[] = [
  'filter-period',
  'filter-date-range',
  'filter-status',
  'filter-establishment',
];

/** Human-readable label per filter group (configure panel). */
export const FILTER_GROUP_LABELS: Record<FilterGroupId, string> = {
  'filter-period': 'Période',
  'filter-date-range': 'Date personnalisée',
  'filter-status': 'Statut',
  'filter-establishment': 'Établissement',
};

/** React Query keys for event-summary list + getById (shared with the {@link DataTable} expand). */
export { DATE_RANGE_PERIOD_OPTIONS } from '@/constants/shared-catalog';

/** React Query keys invalidated after an event-summary create / save. */
export const EVENT_SUMMARY_FORM_QUERY_KEYS = {
  list: ['event-summaries'] as const,
  one: (id: string) => ['event-summary', id] as const,
};

/** Tab id for the event-summaries list. */
export const EVENT_SUMMARY_TAB_ID = 'tab-summaries';
